import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { CTA_CTE, MERCADO_PAGO } from '../../../constants/mediosEnviosPagos.js'
import {
  ID_ESTADO_EN_ESPERA,
  ID_ESTADO_ENTREGADO,
  ID_ESTADO_ENVIADO,
  ID_ESTADO_NO_CONFIRMADO,
  ID_ESTADO_PERCEPCIONES_NO_OK,
  ID_ESTADO_PERCEPCIONES_OK
} from '../../../constants/pedidoEstados'
import { toastErrorLog } from '../../../helpers/toastErrorLog'
import { useAuth } from '../../../hooks/useAuth'
import { setModal } from '../../../slices/modalSlice'
import { DetallePrecio } from '../../compras/DetallePrecio'
import { calcularTotalItemConIva, calcularTotalItemSinIva } from '../../helpers/calcularPrecioTotal'
import * as archivoAdjuntoService from '../../servicios/archivoAdjuntoService'
import * as impuestoService from '../../servicios/impuestoService'
import * as mensajesService from '../../servicios/mensajesService'
import * as pedidosService from '../../servicios/pedidosService'
import BordeDetalle from '../../ui/BordeDetalle'
import { MensajeriaNew } from '../mensajeria/MensajeriaNew'
import { GenericModal } from '../modal/GenericModal'
import { PasarelaMP } from '../pasarelaPagos/PasarelaMP'
import { adjuntoBuilder } from '../upload/adjuntoBuilder'
import { Upload } from '../upload/Upload'
import styles from './Pedido.module.css'

const TIPO_IMPUESTO_PERCEPCION = 'P'
const ALICUOTA_MAX = 100
const MENSAJE_PEDIDO_COMPRA = 12
const MENSAJE_PEDIDO_VENTA = 13
const AUMENTO_TIENDA_OFICIAL = 1
const ADJUNTO_FACTURA = 11
const PAGO_ANTICIPO = 7

const recalcularMonto = (base, alicuota) => (base * alicuota) / 100

const PedidoDetalle = () => {
  const navigate = useNavigate()
  const { idPedido } = useParams()
  const { state } = useLocation()
  const dispatch = useDispatch()

  const [percepciones, setPercepciones] = useState([])
  const [filasImpuestos, setFilasImpuestos] = useState([])
  const [erroresImpuestos, setErroresImpuestos] = useState({})
  const [modalYaMostrado, setModalYaMostrado] = useState(false)
  const [items, setItems] = useState([])
  const [pedidoState, setPedidoState] = useState(state?.pedido || {})
  const [mensajes, setMensajes] = useState([])

  const { loginDataContext: { idUsuario: usuario } = {} } = useAuth()

  useEffect(() => {
    if (state?.pedido) {
      setItems(state.pedido.pedidoItems)
      setPedidoState({
        ...state.pedido,
        esVentas: state.esVentas ?? false
      })
    } else {
      obtenerPedido()
    }
  }, [state?.pedido, idPedido])

  const obtenerPedido = async () => {
    const { data } = await pedidosService.buscarPorIdPedido(idPedido)
    const items = data.pedidoItems
    if (items.length > 0) {
      const esVentas = data.idVendedor === usuario

      setItems(items)
      setPedidoState({
        idPedido,
        idComprador: data.idComprador,
        idVendedor: data.idVendedor,
        fechaPedido: data.fechaPedido,
        esVentas: esVentas
      })
    }
  }
  useEffect(() => {
    setModalYaMostrado(false)
  }, [idPedido])

  useEffect(() => {
    if (state?.mostrarModalImpuestos && !modalYaMostrado) {
      setTimeout(() => {
        dispatch(
          setModal({
            show: true,
            title: 'Observación de Impuestos',
            body: 'Para continuar, comuníquese con el vendedor y defina los impuestos aplicables. Si corresponde, podrá adjuntar la documentación necesaria para exenciones o percepciones.',
            type: 'mensaje',
            size: 'sm'
          })
        )
        setModalYaMostrado(true)
      }, 100)
    }
  }, [state?.mostrarModalImpuestos, modalYaMostrado, dispatch])

  if (!pedidoState) {
    return null
  }

  const { idComprador, idVendedor } = pedidoState

  const validaCampos = (filas) => {
    const errores = {}
    ;(filas ?? []).forEach((f, i) => {
      const esNueva = !f.existente

      const vaciaNueva =
        esNueva &&
        !f.idProvincia &&
        (f.alicuota === '' || f.alicuota === null) &&
        (!f.itemsBaseIds || f.itemsBaseIds.length === 0)

      if (vaciaNueva) {
        return
      }

      if (esNueva && !f.idProvincia) {
        errores[`provincia_${i}`] = 'Debe seleccionar una provincia'
      }

      if (!f.pendienteEliminar && (!f.alicuota || f.alicuota <= 0 || f.alicuota > ALICUOTA_MAX)) {
        errores[`alicuota_${i}`] =
          `La alicuota debe ser mayor a 0 y menor o igual a ${ALICUOTA_MAX}`
      }

      if (esNueva && (!f.itemsBaseIds || f.itemsBaseIds.length === 0)) {
        errores[`items_${i}`] = 'Debe seleccionar al menos un ítem para calcular la base imponible'
      }
    })

    const provinciasExistentes = new Set()
    ;(filas ?? []).forEach((f, i) => {
      if (f.pendienteEliminar) {
        return
      }
      const id = f.idProvincia
      if (!id) {
        return
      }

      const key = `${id}`
      if (provinciasExistentes.has(key)) {
        errores[`provincia_${i}`] = 'Ya existe una alicuota para la provincia seleccionada'
      } else {
        provinciasExistentes.add(key)
      }
    })

    return errores
  }

  const esMayorista = (item) => {
    if (!item?.preciosMayoristas || item.preciosMayoristas.length === 0) {
      return null
    }
    const tiersValidos = item.preciosMayoristas
      .map((p) => ({
        cantidad: p.cantidad,
        precioUnitario: p.precioUnitario
      }))
      .filter((p) => p.cantidad > 0 && p.precioUnitario > 0)

    if (tiersValidos.length === 0) {
      return null
    }

    const aplicables = tiersValidos.filter((tier) => item.cantidad >= tier.cantidad)
    if (aplicables.length === 0) {
      return null
    }

    return aplicables.reduce((prev, curr) => (curr.cantidad > prev.cantidad ? curr : prev))
  }

  const valorUnitarioSinIva = (item) => {
    const mayorista = esMayorista(item)
    if (mayorista) {
      return mayorista.precioUnitario
    }

    const unitario = item.precioUnitario
    if (unitario > 0) {
      return unitario
    }
    const totalConIva = item.valorTotalConIva
    const cant = item.cantidad || 1
    return cant > 0 ? totalConIva / cant : 0
  }

  const total = useMemo(
    () => items.reduce((acc, item) => acc + calcularTotalItemConIva(item), 0),
    [items]
  )

  const totalSinIva = items.reduce((acc, item) => acc + calcularTotalItemSinIva(item), 0)

  const costoEnvioTotal = (() => {
    const preciosUnicos = new Set()

    items.forEach((item) => {
      const precio = Number(item.precioEnvio) || 0
      if (precio > 0) {
        preciosUnicos.add(precio)
      }
    })

    return preciosUnicos.size > 0 ? Math.max(...Array.from(preciosUnicos)) : 0
  })()

  const netoTotalPedido = totalSinIva + costoEnvioTotal

  const itemsMP = {
    items: items.map((item) => {
      const anticipo = !item.anticipo
        ? 100
        : item.idPagoEstado === PAGO_ANTICIPO
          ? 100 - item.anticipo
          : item.anticipo
      return {
        title: item.titulo,
        quantity: item.cantidad,
        unit_price: item.precioUnitario * (1 + item.alicuota / 100) * (anticipo / 100),
        fee_percentage: item.esTiendaOficial
          ? (item.porcentajeExposicion + AUMENTO_TIENDA_OFICIAL) / 100
          : item.porcentajeExposicion / 100
      }
    }),
    orderId: idPedido,
    metadata: { pedidoItems: items.map((item) => item.idPedidoItem) },
    email: items[0]?.vendedorEMail || '',
    idVendedor
  }

  const obtenerImpuestos = async () => {
    const { data } = await impuestoService.buscarTodos(idPedido)
    setPercepciones(data)
    setFilasImpuestos(
      data.map((i) => ({
        ...i,
        existente: true,
        pendienteEliminar: false,
        itemsBaseIds: []
      }))
    )
    setErroresImpuestos({})
  }

  const buscarMensajes = async () => {
    if (!idComprador) {
      return
    }

    try {
      const { data } = await mensajesService.buscarPorPedido(idPedido, idComprador)
      setMensajes(data.sort((a, b) => new Date(a.fechaEscrito) - new Date(b.fechaEscrito)))
    } catch {
      setMensajes([])
    }
  }

  useEffect(() => {
    if (idComprador) {
      obtenerImpuestos()
      buscarMensajes()
    }
  }, [idComprador, idPedido])

  const actualizarPedido = async () => {
    try {
      if (items.length === 0) {
        return
      }
      const { data } = await pedidosService.buscarPorPedidoItem(items[0].idPedidoItem)
      setItems(data.pedidoItems)
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar el pedido.`
      toastErrorLog(error, mensaje, 'compra')
    }
  }

  const notificar = async (observacionImpuestos = false) => {
    try {
      const itemPercepcionOK = items.find(
        (item) => item.idPedidoItemEstado === ID_ESTADO_PERCEPCIONES_OK
      )
      let nuevosItems = [...items]

      const mensaje = {
        idMensajePadre: null,
        idPublicacion: null,
        idPedido: idPedido,
        idPresupuesto: null,
        idComprador,
        idVendedor,
        idUsuarioChat: observacionImpuestos ? idVendedor : idComprador,
        texto: !observacionImpuestos
          ? !itemPercepcionOK
            ? 'Se actualizaron las percepciones. Revisar el detalle de impuestos del pedido.'
            : 'Las percepciones fueron verificadas y confirmadas.'
          : 'Por favor revisar las percepciones aplicadas, ya que presentan observaciones.',
        privado: true,
        idAdjunto: null
      }

      await mensajesService.insertar(mensaje)

      for (const item of items) {
        const estadoActualizado = {
          idPedidoItem: item.idPedidoItem,
          idVendedor,
          idPedidoItemEstado: observacionImpuestos
            ? ID_ESTADO_PERCEPCIONES_NO_OK
            : ID_ESTADO_EN_ESPERA
        }

        const { data: itemActualizado } =
          await pedidosService.actualizarIdPedidoEstadoItem(estadoActualizado)

        nuevosItems = nuevosItems.map((i) =>
          i.idPedidoItem === itemActualizado.idPedidoItem ? { ...i, ...itemActualizado } : i
        )
      }

      setItems(nuevosItems)
    } catch (error) {
      const mensaje = `Ocurrió un error al enviar la notificación.`
      toastErrorLog(error, mensaje, 'compra')
    }
  }

  const observarImpuestos = async () => {
    await notificar(true)
    await buscarMensajes()
    toast.success('Se enviaron las observaciones sobre los impuestos')
  }

  const ingresarPercepciones = async () => {
    const errores = validaCampos(filasImpuestos)
    if (Object.keys(errores).length > 0) {
      setErroresImpuestos(errores)
      return false
    }
    const nuevas = filasImpuestos.filter(
      (f) => !f.existente && f.idProvincia && f.alicuota !== '' && f.alicuota !== null
    )

    const eliminadas = filasImpuestos.filter((f) => f.existente && f.pendienteEliminar)
    const actualizables = filasImpuestos.filter((f) => f.existente && !f.pendienteEliminar)

    await insertarNuevas(nuevas)
    await eliminarExistentes(eliminadas)
    await actualizarExistentes(actualizables)

    const { data } = await impuestoService.buscarTodos(idPedido)
    setPercepciones(data)

    setFilasImpuestos(
      data.map((i) => ({
        ...i,
        existente: true,
        pendienteEliminar: false,
        itemsBaseIds: []
      }))
    )

    setErroresImpuestos({})
    return true
  }

  const insertarNuevas = async (nuevas) => {
    try {
      if (nuevas.length === 0) {
        return
      }

      for (const f of nuevas) {
        const baseImponible = f.baseImponible || netoTotalPedido
        await impuestoService.insertar({
          idPedido,
          idProvincia: f.idProvincia,
          tipoImpuesto: TIPO_IMPUESTO_PERCEPCION,
          alicuota: f.alicuota,
          baseImponible: baseImponible,
          monto: recalcularMonto(baseImponible, f.alicuota)
        })
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al insertar las percepciones.`
      toastErrorLog(error, mensaje, 'impuesto')
    }
  }

  const eliminarExistentes = async (eliminadas) => {
    if (eliminadas.length === 0) {
      return
    }

    try {
      for (const f of eliminadas) {
        await impuestoService.eliminar({
          idPedido,
          idProvincia: f.idProvincia,
          tipoImpuesto: TIPO_IMPUESTO_PERCEPCION
        })
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar las percepciones.`
      toastErrorLog(error, mensaje, 'impuesto')
    }
  }

  const actualizarExistentes = async (actualizables) => {
    if (actualizables.length === 0) {
      return
    }

    try {
      for (const f of actualizables) {
        await impuestoService.actualizar({
          idPedido,
          idProvincia: f.idProvincia,
          tipoImpuesto: TIPO_IMPUESTO_PERCEPCION,
          alicuota: f.alicuota,
          baseImponible: f.baseImponible,
          monto: recalcularMonto(f.baseImponible, f.alicuota)
        })
      }
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar las percepciones.`
      toastErrorLog(error, mensaje, 'impuesto')
    }
  }

  const confirmarYEnviar = async () => {
    try {
      const ok = await ingresarPercepciones()
      if (!ok) {
        return
      }

      await notificar()
      if (!pedidoState.esVentas) {
        await actualizarPedido()
      }
      await buscarMensajes()
      toast.success('Se confirmó y envió correctamente al comprador')
    } catch (error) {
      const mensaje = `Ocurrió un error al confirmar y enviar el pedido.`
      toastErrorLog(error, mensaje, 'compra')
    }
  }

  const habilitarBotonVendedor = items.some(
    (item) =>
      item.idPedidoItemEstado === ID_ESTADO_PERCEPCIONES_OK ||
      item.idPedidoItemEstado === ID_ESTADO_PERCEPCIONES_NO_OK
  )

  const habilitarBotonComprador = items.some(
    (item) => item.idPedidoItemEstado === ID_ESTADO_EN_ESPERA
  )

  const deshabilitarBotonCancelar = items.some(
    (item) =>
      item.idPedidoItemEstado === ID_ESTADO_ENTREGADO ||
      item.idPedidoItemEstado === ID_ESTADO_ENVIADO
  )

  const esCuentaCorrienteNoConfirmado =
    items[0]?.idMedioPago === CTA_CTE &&
    items.some((item) => item.idPedidoItemEstado === ID_ESTADO_NO_CONFIRMADO)

  const openConfirm = () => {
    dispatch(
      setModal({
        size: 'sm',
        title: 'Confirmar eliminación',
        type: 'botones',
        body: '¿Estás seguro de que querés eliminar el pedido en su totalidad?',
        show: true
      })
    )
  }

  const eliminarPedido = async () => {
    try {
      await pedidosService.eliminarPedido(idPedido, idVendedor)
      toast.success('Pedido eliminado correctamente')
      setTimeout(() => navigate('/Pedidos'), 100)
      return true
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar el pedido.`
      toastErrorLog(error, mensaje, 'pedidos')
      return false
    }
  }

  const [adjuntoPendiente, setAdjuntoPendiente] = useState(null)
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false)

  useEffect(() => {
    const fetchAdjuntoFactura = async () => {
      if (!idPedido || !pedidoState) return
      const idParaBuscar = pedidoState.esVentas ? usuario : pedidoState.idVendedor
      if (!idParaBuscar) return
      const { data } = await archivoAdjuntoService.buscarUltimoAdjunto(
        idParaBuscar,
        ADJUNTO_FACTURA,
        idPedido
      )
      const adj = data?.adjunto || data
      if (!adj?.idAdjunto) return
      const { data: dataAdj } = await archivoAdjuntoService.buscarAdjuntosPorId(adj.idAdjunto)
      const { dataAdjunto, adjunto: datosAdjunto } = dataAdj
      const previewAdjunto = await adjuntoBuilder(
        dataAdjunto,
        datosAdjunto,
        datosAdjunto?.archivo,
        { preview: true }
      )
      setAdjuntoPendiente({ ...previewAdjunto, idAdjunto: adj.idAdjunto })
    }
    if (idPedido && pedidoState) {
      fetchAdjuntoFactura()
    }
  }, [idPedido, usuario, pedidoState])

  const handleCarga = async (adjunto) => {
    try {
      setSubiendoAdjunto(true)
      const idAdjunto =
        adjunto?.data?.idAdjunto || adjunto?.data?.id || adjunto?.data?.adjunto?.idAdjunto
      if (!idAdjunto) {
        toast.error('No se pudo obtener el ID del adjunto')
        return
      }
      const { data } = await archivoAdjuntoService.buscarAdjuntosPorId(idAdjunto)
      const { dataAdjunto, adjunto: datosAdjunto } = data
      const previewAdjunto = await adjuntoBuilder(
        dataAdjunto,
        datosAdjunto,
        datosAdjunto?.archivo,
        { preview: true }
      )
      setAdjuntoPendiente({ ...previewAdjunto, idAdjunto })
    } catch (error) {
      toastErrorLog(error, 'Error al procesar el adjunto', 'adjunto')
    } finally {
      setSubiendoAdjunto(false)
    }
  }

  const handleEliminar = () => {
    setAdjuntoPendiente(null)
  }

  return (
    <BordeDetalle
      className={styles.pedido}
      titulo={`Pedido Nº ${idPedido}`}
      onCerrarClick={() => navigate(pedidoState?.esVentas ? '/Ventas' : '/Pedidos')}
    >
      <DetallePrecio
        pedidoItems={items}
        valorUnitarioSinIva={valorUnitarioSinIva}
        esMayorista={esMayorista}
        percepciones={percepciones}
        total={total}
        totalSinIva={totalSinIva}
        costoEnvio={costoEnvioTotal}
        usaImagen={false}
        esVentas={pedidoState.esVentas}
        filasImpuestos={filasImpuestos}
        onChangeFilasImpuestos={setFilasImpuestos}
        erroresImpuestos={erroresImpuestos}
        setErroresImpuestos={setErroresImpuestos}
      />

      {(items[0]?.medioPago === 'Mercado Pago' || items[0]?.idMedioPago === MERCADO_PAGO) &&
        !pedidoState.esVentas &&
        items[0]?.idPedidoItemEstado !== ID_ESTADO_PERCEPCIONES_OK &&
        items[0]?.idPedidoItemEstado !== ID_ESTADO_PERCEPCIONES_NO_OK && (
          <PasarelaMP items={itemsMP} />
        )}

      <div className="text-center w-100 mb-2 d-flex justify-content-center mt-3">
        {pedidoState.esVentas ? (
          habilitarBotonVendedor && (
            <button type="button" className={styles.btnConfirmar} onClick={confirmarYEnviar}>
              Confirmar y notificar comprador
            </button>
          )
        ) : (
          <>
            {!deshabilitarBotonCancelar && (
              <button
                type="button"
                className={styles.btnDanger}
                onClick={openConfirm}
                disabled={esCuentaCorrienteNoConfirmado}
              >
                Cancelar compra
              </button>
            )}
            {habilitarBotonComprador && (
              <button
                type="button"
                className={styles.btnObservar}
                onClick={observarImpuestos}
                title="Se le notificará al vendedor la revisión de impuestos"
              >
                Observar impuestos
              </button>
            )}
          </>
        )}
      </div>
      <GenericModal onConfirm={eliminarPedido} />

      <MensajeriaNew
        titulo={'Mensajería interna'}
        id={Number(idPedido)}
        tipoDoc={'pedidoCabecera'}
        idVendedor={idVendedor}
        idComprador={idComprador}
        mensajes={mensajes}
        tipoMensajes={'chat'}
        usuario={usuario}
        actualizarMensajes={buscarMensajes}
        idMensajeTipo={pedidoState.esVentas ? MENSAJE_PEDIDO_COMPRA : MENSAJE_PEDIDO_VENTA}
        pedidoEntregado={deshabilitarBotonCancelar}
      />
      <div className={styles.uploadBox}>
        <span className={styles.uploadBoxIcon} aria-hidden="true">
          <i className="bi bi-file-earmark-arrow-up"></i>
        </span>
        <h5 className={styles.adjuntarFacturaTitulo}>
          {pedidoState.esVentas ? 'Adjuntar factura' : 'Factura de la compra'}
        </h5>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {pedidoState.esVentas && (
            <>
              <Upload
                idDoc={Number(idPedido)}
                idTipo={11}
                idPerfil={usuario}
                onCarga={handleCarga}
                adjuntoPendiente={adjuntoPendiente ? [adjuntoPendiente] : []}
              />
              {subiendoAdjunto && (
                <span className={styles.nombreAdjuntoCargado} style={{ color: '#888' }}>
                  Subiendo adjunto...
                </span>
              )}
            </>
          )}
          {adjuntoPendiente && !subiendoAdjunto && (
            <span className={styles.nombreAdjuntoCargado} title={adjuntoPendiente.nombre}>
              <i className="bi bi-paperclip" style={{ marginRight: 4, color: '#007bff' }} />
              <a
                href={adjuntoPendiente.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.archivoLink}
              >
                {adjuntoPendiente.nombre || 'Archivo'}
              </a>
              {pedidoState.esVentas && (
                <button
                  type="button"
                  className={styles.removeAdjuntoBtn}
                  onClick={handleEliminar}
                  title="Eliminar adjunto"
                  aria-label="Eliminar adjunto"
                >
                  <i className="bi bi-trash" />
                </button>
              )}
            </span>
          )}
          {!adjuntoPendiente && !subiendoAdjunto && !pedidoState.esVentas && (
            <span className={styles.nombreAdjuntoCargado} style={{ color: '#888' }}>
              Factura no disponible
            </span>
          )}
        </div>
      </div>
    </BordeDetalle>
  )
}

export default PedidoDetalle
